/**
 * A malt's flavor scores from its pH class and color (PLAN-FLAVOR D-FL4).
 *
 * Class and color are on every recipe row, custom ones included, so a malt
 * the catalog has never heard of still draws. Tags are a bonus when the row
 * came from the catalog. Potency is how hard a unit of grist pushes: a few
 * percent of roasted barley owns a stout's flavor, and the same share of
 * pilsner malt is lost in the grist.
 *
 * Colour decides within a class, not just the class. A light roast (biscuit,
 * amber, brown) is toast and nuts, not chocolate, and pushes about as hard as
 * a kilned malt; chocolate and coffee start from 150 °L. A dextrin malt
 * (Carafoam, CaraPils) is crystal by process but body, not flavour: it reads
 * a touch sweet and barely pushes at all.
 */

import type { AxisScores, MaltAxis, MaltPhClass } from '@beerwright/core';

type Scores = Partial<Record<MaltAxis, number>>;

function byClass(maltClass: MaltPhClass, lovibond: number): Scores {
  switch (maltClass) {
    case 'base':
      return lovibond < 3 ? { grainy: 4, sweet: 1 } : { grainy: 3, bready: 2 };
    case 'munich-vienna':
      if (lovibond < 8) return { bready: 3, toasty: 2, grainy: 1 };
      if (lovibond < 20) return { bready: 3, toasty: 3, sweet: 1 };
      return { toasty: 4, bready: 3, sweet: 1 };
    case 'wheat-other':
      return { grainy: 3, bready: 2 };
    case 'crystal':
      if (lovibond < 6) return { sweet: 1 };
      if (lovibond < 20) return { caramel: 2, sweet: 3 };
      if (lovibond < 60) return { caramel: 3, sweet: 2, toasty: 1 };
      if (lovibond < 100) return { caramel: 4, darkFruit: 2, toasty: 1 };
      return { caramel: 3, darkFruit: 4, roast: 1 };
    case 'roasted':
      if (lovibond < 60) return { toasty: 3, bready: 2, nutty: 2 };
      if (lovibond < 150) return { toasty: 3, nutty: 2, chocolate: 1, roast: 1 };
      if (lovibond < 250) return { chocolate: 4, roast: 2, nutty: 1 };
      if (lovibond < 400) return { chocolate: 3, roast: 4 };
      return { roast: 5, chocolate: 2 };
    case 'acidulated':
      return { grainy: 2 };
    case 'none':
      return {};
  }
}

/** Tag → axis and the score it sets at least, or raises by one if already higher. */
const TAG_AXES: Readonly<Record<string, readonly [MaltAxis, number]>> = {
  smoked: ['smoke', 4],
  biscuit: ['bready', 3],
  toasted: ['toasty', 3],
  toffee: ['caramel', 3],
  nutty: ['nutty', 3],
  coffee: ['roast', 3],
  chocolate: ['chocolate', 3],
  honey: ['sweet', 4],
  melanoidin: ['toasty', 3],
};

export function maltRuleScores(malt: {
  readonly maltClass: MaltPhClass;
  readonly colorLovibond: number;
  readonly tags: readonly string[];
}): AxisScores<'malt'> {
  const scores: Scores = { ...byClass(malt.maltClass, malt.colorLovibond) };
  for (const tag of malt.tags) {
    const hit = TAG_AXES[tag];
    if (hit === undefined) continue;
    const [axis, floor] = hit;
    const before = scores[axis];
    scores[axis] = before === undefined ? floor : Math.min(5, Math.max(before + 1, floor));
  }
  return scores;
}

const POTENCY: Readonly<Record<MaltPhClass, number>> = {
  base: 1,
  'wheat-other': 1,
  acidulated: 1,
  'munich-vienna': 1.5,
  crystal: 4,
  roasted: 8,
  none: 0,
};

/** A roast lighter than this pushes like a kilned malt, not like chocolate malt. */
const LIGHT_ROAST_BELOW_L = 150;
/** A crystal paler than this is a dextrin malt: body, not flavour. */
const DEXTRIN_BELOW_L = 6;

/**
 * How hard a unit of this malt pushes its axes. Core's `FLAVOR_CURVE` malt k was
 * calibrated against these multipliers, so changing them moves every malt wheel.
 * `flavor-calibration.test.ts` in commands pins the result.
 */
export function maltPotency(maltClass: MaltPhClass, colorLovibond: number): number {
  if (maltClass === 'roasted' && colorLovibond < LIGHT_ROAST_BELOW_L) return 2;
  if (maltClass === 'crystal' && colorLovibond < DEXTRIN_BELOW_L) return 1;
  return POTENCY[maltClass];
}
